/**
 * Parcela Flex calculation.
 *
 * Servopa "Flex" reduces the parcela by applying the discount to the credit
 * (not to the parcela itself), then re-multiplying by the monthly admin rate.
 *
 *     parcela_flex_sem_seguro = credito * (1 - desconto_flex) * taxa_mensal
 *
 * Equivalently (and what we use for full precision, since `parcela_sem_seguro`
 * is already `credito * taxa_mensal` rounded by Servopa to the cent):
 *
 *     parcela_flex_sem_seguro = parcela_sem_seguro * (1 - desconto_flex)
 *
 * The two forms agree within Servopa's ±R$0,50 cent-rounding tolerance. The
 * `parcela_sem_seguro` form preserves the rate Servopa actually used to
 * compute the row, regardless of how many decimals the displayed
 * `percentual_mensal` was truncated to.
 *
 * Insurance is a fixed addon — the flex discount must NOT reduce it.
 */

export const FLEX_PERCENTAGES = [10, 20, 30, 40, 50] as const;
export type FlexPercentage = (typeof FLEX_PERCENTAGES)[number];

export interface FlexCalcInput {
  parcelaSemSeguro: number;
  valorSeguro: number;
  hasInsurance: boolean;
}

/** Parcela normal (whole credit, no discount). Adds seguro when applicable. */
export function calcParcelaNormal({ parcelaSemSeguro, valorSeguro, hasInsurance }: FlexCalcInput): number {
  return hasInsurance ? parcelaSemSeguro + valorSeguro : parcelaSemSeguro;
}

/** Parcela flex with the given discount percentage (typically 10/20/30/40/50,
 *  but any numeric percentage is valid for the simulator's free-form input). */
export function calcParcelaFlex(
  input: FlexCalcInput,
  flexPercentage: number,
): number {
  const flexSemSeguro = input.parcelaSemSeguro * (1 - flexPercentage / 100);
  return input.hasInsurance ? flexSemSeguro + input.valorSeguro : flexSemSeguro;
}

/**
 * Derive the effective monthly admin rate from a category row.
 * Returns `parcela_sem_seguro / valor_credito`, which carries the full
 * precision Servopa used (not the truncated 4-decimal display value).
 */
export function deriveTaxaMensal(parcelaSemSeguro: number, valorCredito: number): number {
  if (!valorCredito) return 0;
  return parcelaSemSeguro / valorCredito;
}
